/**
 * Fork-local translations.
 *
 * The published scratch-l10n catalog does not carry this fork's strings, so we
 * merge these into the locale catalog ourselves (see reducers/locales.js). The
 * same catalog feeds both react-intl (the `gui.extension.mcremote.*` library
 * tile, and any other fork-added `gui.*` string) and the VM's format-message
 * (the `mcremote.*` block labels), so one entry per id covers both.
 *
 * Most entries here are McRemote-extension-specific, but this file also
 * carries translations for a few pre-existing upstream Scratch string ids
 * that scratch-l10n's Japanese catalog happens not to include yet (e.g.
 * `gui.sharedMessages.loadFromComputerTitle`, `gui.menuBar.theme`,
 * `gui.menuBar.colorMode`) — same mechanism, just not a fork addition.
 *
 * English is defined where the strings live (`defaultMessage` in the extension
 * library entry, `default:` in the VM extension's getInfo) and is used as the
 * fallback, so only translations belong here.
 *
 * Routine when adding a McRemote block: add the block's message id and its
 * Japanese and Japanese Hiragana text below, keeping the `[PLACEHOLDER]` tokens
 * identical to the block's `default:` text in scratch-vm. Ids whose translation
 * equals the English brand name (e.g. the `mc-remote` category) are intentionally
 * omitted and fall back to English.
 */
const mcremoteMessages = {
    'ja': {
        // Editor UI strings (not McRemote-specific) that scratch-l10n's ja
        // catalog doesn't carry yet.
        'gui.sharedMessages.loadFromComputerTitle': 'コンピューターから読み込む',
        'gui.menuBar.theme': 'テーマ',
        'gui.menuBar.colorMode': 'カラーモード',
        // Local project/sprite browser save (McRemote save-track entry gate / b6).
        'gui.menuBar.browserSavedProjects': 'ブラウザに保存した作品',
        'gui.menuBar.deleteBrowserSavedProject': 'このブラウザ保存の作品を削除する',
        'gui.menuBar.browserSavedSprites': 'ブラウザに保存したスプライト',
        'gui.menuBar.deleteBrowserSavedSprite': 'このブラウザ保存のスプライトを削除する',
        'gui.menuBar.storagePersistGranted': '永続化あり',
        'gui.menuBar.storagePersistNotGranted': '永続化なし',
        'gui.projectLoader.saveError': '今の作品をブラウザに保存できませんでした。新しい作品は開いていません。',
        'gui.spriteSelectorItem.contextMenuSaveToBrowser': 'ブラウザ保存',
        'gui.mcremote.notice.version': 'バージョン {version}',
        'gui.mcremote.notice.homepageLink': 'ホームページ',
        'gui.extension.mcremote.description':
            'Minecraft世界と双方向でつながり、自動建築や自動演奏をしたり、イベントを感知する。',
        'gui.alerts.mcremoteConnectionDisabled': 'このショーケースでは Minecraft の操作が無効です。',
        'gui.alerts.mcremoteNotConnected': 'まず mc-remote の「接続する」ブロックを実行してください。',
        'gui.alerts.mcremoteInvalidBuildMode': '建築モードは DEBUG、TRACE、FAST から選んでください。',
        'gui.alerts.mcremoteInvalidTraceDelay': 'TRACEの待ち時間は0秒から2秒の数値にしてください。',
        'gui.alerts.mcremoteBuildDeliveryFailed':
            'ブロック設置の送信が混み合ったため、mc-remoteとの接続を停止しました。もう一度接続してください。',
        'gui.alerts.mcremoteServerBackpressure':
            'mc-remoteサーバーが混み合い、この操作を受け付けませんでした。少し待って、もう一度試してください。',
        'gui.alerts.mcremoteGroundNotFound': '地面が見つかりません。',
        'gui.alerts.mcremoteOperationFailed': 'Minecraftの操作に失敗しました。mc-remoteエラーを確認してください。',
        'gui.alerts.mcremoteCatalogUnavailable':
            'カタログを取得できません。Minecraftへの接続を確認して、もう一度実行してください。',
        'gui.aria.mcremoteConnectionMenu': 'mc-remote 接続先メニュー',
        'gui.menuBar.mcremoteConnection': '接続先',
        'gui.mcremote.blockPicker.blockId': 'ブロックID',
        'gui.mcremote.blockPicker.catalogStatus': 'カタログ状態',
        'gui.mcremote.blockPicker.stateText': '状態',
        'gui.mcremote.blockPicker.cancel': 'キャンセル',
        'gui.mcremote.blockPicker.defaultExplanation':
            'デフォルト値は状態欄から省略します。ブロック設置時はブロックデータ全体を置き換え、既存の状態とはマージしません。',
        'gui.mcremote.blockPicker.empty':
            '利用できるカタログのブロックがありません。上の欄へ自由に入力できます。',
        'gui.mcremote.blockPicker.minecraftDefaultValue': '{value}（デフォルト）',
        'gui.mcremote.blockPicker.noMatches': '検索に一致するブロックがありません。',
        'gui.mcremote.blockPicker.reporterConnected':
            'レポーターブロックまたは変数が接続されています。picker は取り外しません。両方の入力を文字列へ戻してから適用してください。',
        'gui.mcremote.blockPicker.resultCount': '{count}件',
        'gui.mcremote.blockPicker.search': 'ブロックID・名前で検索',
        'gui.mcremote.blockPicker.stateHeading': 'ブロックの状態',
        'gui.mcremote.blockPicker.statusCurrent': '取得済み — {version} · {source} · {hash}',
        'gui.mcremote.blockPicker.statusNotAcquired':
            '未取得 — Minecraft に接続するとブロックを選べます。',
        'gui.mcremote.blockPicker.statusUnavailable':
            '利用不可 — 接続先からカタログを取得できませんでした。',
        'gui.mcremote.blockPicker.title': 'Minecraft のブロックを選ぶ',
        'gui.mcremote.blockPicker.apply': 'この値の組を使う',
        'gui.mcremote.notice.collapse': 'お知らせを閉じる',
        'gui.mcremote.notice.expand': 'お知らせを開く',
        'gui.mcremote.wireScope.actionConnect': 'まず「接続する」ブロックを実行してください。',
        'gui.mcremote.wireScope.actionConnectionDisabled': 'このショーケースページでは Minecraft の操作が無効です。',
        'gui.mcremote.wireScope.actionPairAgain': '「接続する」ブロックを実行し、もう一度ペアリングしてください。',
        'gui.mcremote.wireScope.actionProtocolMismatch': 'Scratch と mc-remote サーバーのバージョンを揃えてください。',
        'gui.mcremote.wireScope.actionRequired': '次にすること',
        'gui.mcremote.wireScope.actionRetry': '接続を確認して、もう一度試してください。',
        'gui.mcremote.wireScope.configuredTarget': '設定先',
        'gui.mcremote.wireScope.actualTarget': '実接続先',
        'gui.mcremote.wireScope.reconnectStatus': '再接続',
        'gui.mcremote.wireScope.reconnectNeeded': '要',
        'gui.mcremote.wireScope.reconnectNotNeeded': '不要',
        'gui.mcremote.wireScope.reconnectPending': 'ペア待ち…',
        'gui.mcremote.wireScope.collapse': 'WireScope mini を閉じる',
        'gui.mcremote.wireScope.expand': 'WireScope mini を開く',
        'gui.mcremote.wireScope.observationTarget': '観察対象',
        'gui.mcremote.wireScope.openIndependent': 'WireScope を開く',
        'gui.mcremote.wireScope.pairCode': 'ペアコード',
        'gui.mcremote.wireScope.pairCommand': 'ペアリングコマンド',
        'gui.mcremote.wireScope.status': '状態',
        'gui.mcremote.wireScope.statusClosed': '切断',
        'gui.mcremote.wireScope.statusConnected': '接続',
        'gui.mcremote.wireScope.statusDisconnected': '未接続',
        'gui.mcremote.wireScope.statusError': 'エラー',
        'gui.mcremote.wireScope.statusPairing': 'ペア待ち',
        'mcremote.connect': '接続する',
        'mcremote.connectTo': '[NAME] に接続する',
        'mcremote.connectionDisabled': 'このページでは Minecraft への接続が無効です。',
        'mcremote.pairCode': 'ペアコード',
        'mcremote.pairCommand': 'ペアリングコマンド',
        'mcremote.whenPaired': 'ペアリングできたとき',
        'mcremote.setDimension': '建築する次元を [DIMENSION] にする',
        'mcremote.setBuildOrigin': '建築原点 (x, y, z) を[X],  0,  [Z]にする',
        'mcremote.setBuildMode': '建築モードを [MODE] にする（TRACEの待ち時間 [TRACE_DELAY] 秒）',
        'mcremote.flushBuildCommands': '送ったブロック設置が終わるまで待つ',
        'mcremote.buildMode.debug': 'DEBUG（確認）',
        'mcremote.buildMode.trace': 'TRACE（過程）',
        'mcremote.buildMode.fast': 'FAST（高速）',
        'mcremote.dimension.overworld': 'オーバーワールド',
        'mcremote.dimension.nether': 'ネザー',
        'mcremote.dimension.theEnd': 'エンド',
        'mcremote.postToChat': 'チャットに [MSG] と言う',
        'mcremote.setBlock': 'x :[X]  y :[Y]  z :[Z] に ブロックID [BLOCK] 状態 [STATE] を置く [PICKER]',
        'mcremote.setBlocks':
            'x :[X1]  y :[Y1]  z :[Z1] から x :[X2]  y :[Y2]  z :[Z2] まで ブロックID [BLOCK] 状態 [STATE] を置く [PICKER]',
        'mcremote.getBlock': 'x :[X]  y :[Y]  z :[Z] のブロック情報',
        'mcremote.getBlocks':
            'x :[X1]  y :[Y1]  z :[Z1] から x :[X2]  y :[Y2]  z :[Z2] までのブロック情報を [LIST] に入れる',
        'mcremote.getHeight': 'x :[X]  z :[Z] の地面の高さ',
        'mcremote.catalogToList': 'カタログの [KIND] を [LIST] に入れる',
        'mcremote.catalogKind.block': 'ブロックID',
        'mcremote.catalogKind.entity': 'エンティティID',
        'mcremote.catalogKind.particle': 'パーティクルID',
        'mcremote.getHeightBelow': 'x :[X]  z :[Z] の y :[MAX_Y] 以下の地面の高さ',
        'mcremote.blockInfoId': '[BLOCK_INFO] のブロックID',
        'mcremote.blockInfoState': '[BLOCK_INFO] の状態',
        'mcremote.blockInfoStateProperty': '[BLOCK_INFO] の状態 [PROPERTY]',
        'mcremote.blockInfoHasStateProperty': '[BLOCK_INFO] に状態 [PROPERTY] がある',
        'mcremote.getSign': '看板 x :[X]  y :[Y]  z :[Z] の情報',
        'mcremote.setSign': '看板 x :[X]  y :[Y]  z :[Z] の[FACE]を [LINE0] [LINE1] [LINE2] [LINE3] にする',
        'mcremote.updateSignLine': '看板 x :[X]  y :[Y]  z :[Z] の[FACE] [LINE]を [TEXT] にする',
        'mcremote.signLineText': '[SIGN_INFO] の[FACE] [LINE]の文字',
        'mcremote.signLineColor': '[SIGN_INFO] の[FACE] [LINE]の色',
        'mcremote.signLineHasDecoration': '[SIGN_INFO] の[FACE] [LINE]に[DECORATION]がある',
        'mcremote.signIsWaxed': '[SIGN_INFO] はロウ引きされている',
        'mcremote.signFace.front': '正面',
        'mcremote.signFace.back': '背面',
        'mcremote.signLineIndex.0': '1行目',
        'mcremote.signLineIndex.1': '2行目',
        'mcremote.signLineIndex.2': '3行目',
        'mcremote.signLineIndex.3': '4行目',
        'mcremote.signDecoration.bold': '太字',
        'mcremote.signDecoration.italic': '斜体',
        'mcremote.signDecoration.obfuscated': 'ランダム',
        'mcremote.signDecoration.strikethrough': '取り消し線',
        'mcremote.signDecoration.underlined': '下線',
        'mcremote.isMcRemoteError': '[VALUE] はmc-remoteエラー',
        'mcremote.spawnParticle':
            'パーティクル [PARTICLE] を x :[X]  y :[Y]  z :[Z] ずれ ' +
            'x[OFFSET_X]  y[OFFSET_Y]  z[OFFSET_Z] 速さ:[SPEED] 数:[COUNT] 表示:[FORCE] で生成する',
        'mcremote.particleVisibility.far': '遠くにも表示',
        'mcremote.particleVisibility.near': '近くだけ表示',
        'mcremote.spawnEntity':
            'エンティティ [ENTITY] を x :[X]  y :[Y]  z :[Z] に生成し ハンドルを [VARIABLE] に入れる',
        'mcremote.getNearbyEntities': 'x :[X]  y :[Y]  z :[Z] の半径 [RADIUS] 以内のエンティティを最大 [MAX_ENTITIES] 件 [LIST] に入れる',
        'mcremote.entityInfo': 'エンティティ情報 [ENTITY_INFO] の [PROPERTY]',
        'mcremote.getEntityPose': 'エンティティ [HANDLE] の姿勢',
        'mcremote.entityPoseInfo': 'エンティティの姿勢 [POSE] の [PROPERTY]',
        'mcremote.setEntityPose': 'エンティティ [HANDLE] を [DIMENSION] x :[X]  y :[Y]  z :[Z] 水平方向:[YAW] 上下方向:[PITCH] へ移動する',
        'mcremote.removeEntity': 'エンティティ [HANDLE] を取り除く',
        'mcremote.particleSpec': '[RECEIVER] に表示するパーティクル [PARTICLE]',
        'mcremote.dustParticleSpec': '[RECEIVER] に表示するダスト 赤 [RED] 緑 [GREEN] 青 [BLUE] 大きさ [SIZE]',
        'mcremote.blockParticleSpec': '[RECEIVER] に表示するブロックのパーティクル [BLOCK] 状態 [STATE]',
        'mcremote.entityProperty.handle': 'ハンドル',
        'mcremote.entityProperty.type': '種類',
        'mcremote.particleReceiver.world': '近くのプレイヤー',
        'mcremote.particleReceiver.self': '接続したプレイヤーだけ',
        'mcremote.playerDirection': 'プレイヤーの向き [AXIS]',
        'mcremote.setPlayerDirection': 'プレイヤーの向きを x[X]  y[Y]  z[Z] にする',
        'mcremote.entityDirection': 'エンティティ [HANDLE] の向き [AXIS]',
        'mcremote.setEntityDirection': 'エンティティ [HANDLE] の向きを x[X]  y[Y]  z[Z] にする',
        'mcremote.strikeLightning': '雷を x :[X]  y :[Y]  z :[Z] に落とす',
        'mcremote.playSound': '音 [SOUND] を x :[X]  y :[Y]  z :[Z] で鳴らす 設定 [OPTIONS]',
        'mcremote.playBlockSound': 'x :[X]  y :[Y]  z :[Z] のブロックの [KIND] 音を鳴らす 設定 [OPTIONS]',
        'mcremote.soundOptions': '音の設定 音量 [VOLUME] 高さ [HEIGHT] 再生先 [RECEIVER]',
        'mcremote.soundKind.place': '設置',
        'mcremote.soundKind.hit': 'たたく',
        'mcremote.soundKind.break': '破壊',
        'mcremote.soundKind.step': '足音',
        'mcremote.soundKind.fall': '着地',
        'mcremote.soundReceiver.world': '近くのプレイヤー',
        'mcremote.soundReceiver.self': '接続したプレイヤーだけ',
        'mcremote.whenPickaxePoke': 'ツルハシでブロックがつつかれたとき',
        'mcremote.whenChatPosted': 'チャットが投稿されたとき',
        'mcremote.whenProjectileHit': '飛び道具が当たったとき',
        'mcremote.eventValue': 'このMinecraftイベントの [PROPERTY]',
        'mcremote.eventValue.sequence': '通し番号',
        'mcremote.eventValue.type': 'イベントの種類',
        'mcremote.eventValue.dimension': '次元',
        'mcremote.eventValue.x': 'x座標',
        'mcremote.eventValue.y': 'y座標',
        'mcremote.eventValue.z': 'z座標',
        'mcremote.eventValue.originX': '原点x',
        'mcremote.eventValue.originY': '原点y',
        'mcremote.eventValue.originZ': '原点z',
        'mcremote.eventValue.face': '面',
        'mcremote.eventValue.hand': '手',
        'mcremote.eventValue.item': 'アイテム',
        'mcremote.eventValue.message': 'メッセージ',
        'mcremote.eventValue.block': 'ブロック情報',
        'mcremote.eventValue.projectile': '飛び道具',
        'mcremote.eventValue.targetKind': '対象の種類',
        'mcremote.eventValue.targetX': '対象のx座標',
        'mcremote.eventValue.targetY': '対象のy座標',
        'mcremote.eventValue.targetZ': '対象のz座標',
        'mcremote.eventValue.targetFace': '対象の面',
        'mcremote.eventValue.targetBlock': '対象のブロック情報',
        'mcremote.eventValue.targetHandle': '対象のエンティティハンドル',
        'mcremote.eventStatus': 'イベント受信の [PROPERTY]',
        'mcremote.eventStatus.cursor': 'カーソル',
        'mcremote.eventStatus.latest': '最新の通し番号',
        'mcremote.eventStatus.overflow': '保持範囲外の欠落数',
        'mcremote.eventStatus.capacity': '容量超過の欠落数',
        'mcremote.eventStatus.discarded': '明示破棄数',
        'mcremote.eventStatus.totalLoss': '欠落合計',
        'mcremote.playerAttribute': 'プレイヤーの [PROPERTY]',
        'mcremote.playerAttribute.dimension': '次元',
        'mcremote.playerAttribute.x': 'x座標',
        'mcremote.playerAttribute.y': 'y座標',
        'mcremote.playerAttribute.z': 'z座標',
        'mcremote.playerAttribute.yaw': '水平方向',
        'mcremote.playerAttribute.pitch': '上下方向',
        'mcremote.setPlayerPos': 'プレイヤーを [DIMENSION] x :[X]  y :[Y]  z :[Z] へ移動する',
        'mcremote.setPlayerPose':
            'プレイヤーを [DIMENSION] x :[X]  y :[Y]  z :[Z] 水平方向:[YAW] 上下方向:[PITCH] へ移動する',
        'mcremote.setPlayerXYZ': 'プレイヤーを x :[X]  y :[Y]  z :[Z] へ移動する'
    },
    'ja-Hira': {
        // Editor UI strings (not McRemote-specific) that scratch-l10n's
        // ja-Hira catalog doesn't carry yet.
        'gui.sharedMessages.loadFromComputerTitle': 'コンピューターからよみこむ',
        'gui.menuBar.theme': 'テーマ',
        'gui.menuBar.colorMode': 'カラーモード',
        // Local project/sprite browser save (McRemote save-track entry gate / b6).
        'gui.menuBar.browserSavedProjects': 'ブラウザにほぞんしたさくひん',
        'gui.menuBar.deleteBrowserSavedProject': 'このブラウザほぞんのさくひんをけす',
        'gui.menuBar.browserSavedSprites': 'ブラウザにほぞんしたスプライト',
        'gui.menuBar.deleteBrowserSavedSprite': 'このブラウザほぞんのスプライトをけす',
        'gui.menuBar.storagePersistGranted': 'えいぞくかあり',
        'gui.menuBar.storagePersistNotGranted': 'えいぞくかなし',
        'gui.projectLoader.saveError': 'いまのさくひんをブラウザにほぞんできませんでした。あたらしいさくひんはひらいていません。',
        'gui.spriteSelectorItem.contextMenuSaveToBrowser': 'ブラウザほぞん',
        'gui.mcremote.notice.version': 'バージョン {version}',
        'gui.mcremote.notice.homepageLink': 'ホームページ',
        'gui.extension.mcremote.description':
            'まいんくらふとのせかいと、そうほうこうでつながり、じどうけんちくや、じどうえんそうをしたり、イベントをかんじとる。',
        'gui.alerts.mcremoteConnectionDisabled': 'このショーケースでは Minecraft をそうさできません。',
        'gui.alerts.mcremoteNotConnected': 'まず mc-remote の「せつぞくする」ブロックをうごかしてください。',
        'gui.alerts.mcremoteInvalidBuildMode': 'けんちくモードは DEBUG、TRACE、FAST からえらんでください。',
        'gui.alerts.mcremoteInvalidTraceDelay': 'TRACEのまちじかんは0びょうから2びょうのすうじにしてください。',
        'gui.alerts.mcremoteBuildDeliveryFailed':
            'ブロックせっちのそうしんがこみあったため、mc-remoteとのせつぞくをとめました。もういちどせつぞくしてください。',
        'gui.alerts.mcremoteServerBackpressure':
            'mc-remoteサーバーがこんでいて、このそうさをうけつけませんでした。すこしまって、もういちどためしてください。',
        'gui.alerts.mcremoteGroundNotFound': 'じめんがみつかりません。',
        'gui.alerts.mcremoteOperationFailed':
            'Minecraftのそうさにしっぱいしました。mc-remoteエラーをかくにんしてください。',
        'gui.alerts.mcremoteCatalogUnavailable':
            'カタログをしゅとくできません。Minecraftへのせつぞくをかくにんして、もういちどじっこうしてください。',
        'gui.aria.mcremoteConnectionMenu': 'mc-remote せつぞくさきメニュー',
        'gui.menuBar.mcremoteConnection': 'せつぞくさき',
        'gui.mcremote.blockPicker.blockId': 'ブロックID',
        'gui.mcremote.blockPicker.catalogStatus': 'カタログじょうたい',
        'gui.mcremote.blockPicker.stateText': 'じょうたい',
        'gui.mcremote.blockPicker.cancel': 'キャンセル',
        'gui.mcremote.blockPicker.defaultExplanation':
            'デフォルトのあたいはじょうたいのらんからしょうりゃくします。ブロックをおくときはブロックデータぜんたいをおきかえ、いまのじょうたいとはまぜません。',
        'gui.mcremote.blockPicker.empty':
            'つかえるカタログのブロックがありません。うえのらんにじゆうににゅうりょくできます。',
        'gui.mcremote.blockPicker.minecraftDefaultValue': '{value}（デフォルト）',
        'gui.mcremote.blockPicker.noMatches': 'さがしているブロックがみつかりません。',
        'gui.mcremote.blockPicker.reporterConnected':
            'レポーターブロックまたはへんすうがせつぞくされています。ピッカーはとりはずしません。りょうほうのにゅうりょくをもじにもどしてからつかってください。',
        'gui.mcremote.blockPicker.resultCount': '{count}けん',
        'gui.mcremote.blockPicker.search': 'ブロックID・なまえでさがす',
        'gui.mcremote.blockPicker.stateHeading': 'ブロックのじょうたい',
        'gui.mcremote.blockPicker.statusCurrent': 'しゅとくずみ — {version} · {source} · {hash}',
        'gui.mcremote.blockPicker.statusNotAcquired':
            'みしゅとく — Minecraft にせつぞくするとブロックをえらべます。',
        'gui.mcremote.blockPicker.statusUnavailable':
            'りようできません — せつぞくさきからカタログをしゅとくできませんでした。',
        'gui.mcremote.blockPicker.title': 'Minecraft のブロックをえらぶ',
        'gui.mcremote.blockPicker.apply': 'このあたいのくみをつかう',
        'gui.mcremote.notice.collapse': 'おしらせをとじる',
        'gui.mcremote.notice.expand': 'おしらせをひらく',
        'gui.mcremote.wireScope.actionConnect': 'まず「せつぞくする」ブロックをうごかしてください。',
        'gui.mcremote.wireScope.actionConnectionDisabled':
            'このショーケースページでは Minecraft をそうさできません。',
        'gui.mcremote.wireScope.actionPairAgain':
            '「せつぞくする」ブロックをうごかし、もういちどペアリングしてください。',
        'gui.mcremote.wireScope.actionProtocolMismatch':
            'Scratch と mc-remote サーバーのバージョンをそろえてください。',
        'gui.mcremote.wireScope.actionRequired': 'つぎにすること',
        'gui.mcremote.wireScope.actionRetry': 'せつぞくをかくにんして、もういちどためしてください。',
        'gui.mcremote.wireScope.configuredTarget': 'せっていしたせつぞくさき',
        'gui.mcremote.wireScope.actualTarget': 'いまのせつぞくさき',
        'gui.mcremote.wireScope.reconnectStatus': 'さいせつぞく',
        'gui.mcremote.wireScope.reconnectNeeded': 'ひつよう',
        'gui.mcremote.wireScope.reconnectNotNeeded': 'ふよう',
        'gui.mcremote.wireScope.reconnectPending': 'ペアリングまち…',
        'gui.mcremote.wireScope.collapse': 'WireScope mini をとじる',
        'gui.mcremote.wireScope.expand': 'WireScope mini をひらく',
        'gui.mcremote.wireScope.observationTarget': 'かんさつするもの',
        'gui.mcremote.wireScope.openIndependent': 'WireScope をひらく',
        'gui.mcremote.wireScope.pairCode': 'ペアコード',
        'gui.mcremote.wireScope.pairCommand': 'ペアリングコマンド',
        'gui.mcremote.wireScope.status': 'じょうたい',
        'gui.mcremote.wireScope.statusClosed': 'せつだん',
        'gui.mcremote.wireScope.statusConnected': 'せつぞく',
        'gui.mcremote.wireScope.statusDisconnected': 'まだせつぞくしていません',
        'gui.mcremote.wireScope.statusError': 'エラー',
        'gui.mcremote.wireScope.statusPairing': 'ペアリングまち',
        'mcremote.connect': 'せつぞくする',
        'mcremote.connectTo': '[NAME] にせつぞくする',
        'mcremote.connectionDisabled': 'このページでは Minecraft にせつぞくできません。',
        'mcremote.pairCode': 'ペアコード',
        'mcremote.pairCommand': 'ペアリングコマンド',
        'mcremote.whenPaired': 'ペアリングできたとき',
        'mcremote.setDimension': 'けんちくするじげんを [DIMENSION] にする',
        'mcremote.setBuildOrigin': 'けんちくのげんてん (x, y, z) を[X],  0,  [Z]にする',
        'mcremote.setBuildMode': 'けんちくモードを [MODE] にする（TRACEのまちじかん [TRACE_DELAY] びょう）',
        'mcremote.flushBuildCommands': 'おくったブロックせっちがおわるまでまつ',
        'mcremote.buildMode.debug': 'DEBUG（かくにん）',
        'mcremote.buildMode.trace': 'TRACE（かてい）',
        'mcremote.buildMode.fast': 'FAST（こうそく）',
        'mcremote.dimension.overworld': 'オーバーワールド',
        'mcremote.dimension.nether': 'ネザー',
        'mcremote.dimension.theEnd': 'エンド',
        'mcremote.postToChat': 'チャットに [MSG] という',
        'mcremote.setBlock':
            'x :[X]  y :[Y]  z :[Z] に ブロックID [BLOCK] じょうたい [STATE] をおく [PICKER]',
        'mcremote.setBlocks':
            'x :[X1]  y :[Y1]  z :[Z1] から x :[X2]  y :[Y2]  z :[Z2] まで ブロックID [BLOCK] じょうたい [STATE] をおく [PICKER]',
        'mcremote.getBlock': 'x :[X]  y :[Y]  z :[Z] のブロックじょうほう',
        'mcremote.getBlocks':
            'x :[X1]  y :[Y1]  z :[Z1] から x :[X2]  y :[Y2]  z :[Z2] までのブロックじょうほうを [LIST] にいれる',
        'mcremote.getHeight': 'x :[X]  z :[Z] のじめんのたかさ',
        'mcremote.catalogToList': 'カタログの [KIND] を [LIST] にいれる',
        'mcremote.catalogKind.block': 'ブロックID',
        'mcremote.catalogKind.entity': 'エンティティID',
        'mcremote.catalogKind.particle': 'パーティクルID',
        'mcremote.getHeightBelow': 'x :[X]  z :[Z] の y :[MAX_Y] いかのじめんのたかさ',
        'mcremote.blockInfoId': '[BLOCK_INFO] のブロックID',
        'mcremote.blockInfoState': '[BLOCK_INFO] のじょうたい',
        'mcremote.blockInfoStateProperty': '[BLOCK_INFO] のじょうたい [PROPERTY]',
        'mcremote.blockInfoHasStateProperty': '[BLOCK_INFO] にじょうたい [PROPERTY] がある',
        'mcremote.getSign': 'かんばん x :[X]  y :[Y]  z :[Z] のじょうほう',
        'mcremote.setSign': 'かんばん x :[X]  y :[Y]  z :[Z] の[FACE]を [LINE0] [LINE1] [LINE2] [LINE3] にする',
        'mcremote.updateSignLine': 'かんばん x :[X]  y :[Y]  z :[Z] の[FACE] [LINE]を [TEXT] にする',
        'mcremote.signLineText': '[SIGN_INFO] の[FACE] [LINE]のもじ',
        'mcremote.signLineColor': '[SIGN_INFO] の[FACE] [LINE]のいろ',
        'mcremote.signLineHasDecoration': '[SIGN_INFO] の[FACE] [LINE]に[DECORATION]がある',
        'mcremote.signIsWaxed': '[SIGN_INFO] はロウびきされている',
        'mcremote.signFace.front': 'しょうめん',
        'mcremote.signFace.back': 'はいめん',
        'mcremote.signLineIndex.0': '1ぎょうめ',
        'mcremote.signLineIndex.1': '2ぎょうめ',
        'mcremote.signLineIndex.2': '3ぎょうめ',
        'mcremote.signLineIndex.3': '4ぎょうめ',
        'mcremote.signDecoration.bold': 'ふとじ',
        'mcremote.signDecoration.italic': 'イタリック',
        'mcremote.signDecoration.obfuscated': 'ランダム',
        'mcremote.signDecoration.strikethrough': 'とりけしせん',
        'mcremote.signDecoration.underlined': 'かせん',
        'mcremote.isMcRemoteError': '[VALUE] はmc-remoteエラー',
        'mcremote.spawnParticle':
            'パーティクル [PARTICLE] を x :[X]  y :[Y]  z :[Z] ずれ ' +
            'x[OFFSET_X]  y[OFFSET_Y]  z[OFFSET_Z] はやさ:[SPEED] かず:[COUNT] ひょうじ:[FORCE] でだす',
        'mcremote.particleVisibility.far': 'とおくにもひょうじ',
        'mcremote.particleVisibility.near': 'ちかくだけひょうじ',
        'mcremote.spawnEntity':
            'エンティティ [ENTITY] を x :[X]  y :[Y]  z :[Z] にだして ハンドルを [VARIABLE] にいれる',
        'mcremote.getNearbyEntities':
            'x :[X]  y :[Y]  z :[Z] のはんけい [RADIUS] いないのエンティティをさいだい [MAX_ENTITIES] けん [LIST] にいれる',
        'mcremote.entityInfo': 'エンティティじょうほう [ENTITY_INFO] の [PROPERTY]',
        'mcremote.getEntityPose': 'エンティティ [HANDLE] のしせい',
        'mcremote.entityPoseInfo': 'エンティティのしせい [POSE] の [PROPERTY]',
        'mcremote.setEntityPose':
            'エンティティ [HANDLE] を [DIMENSION] x :[X]  y :[Y]  z :[Z] よこのむき:[YAW] たてのむき:[PITCH] へうごかす',
        'mcremote.removeEntity': 'エンティティ [HANDLE] をとりのぞく',
        'mcremote.particleSpec': '[RECEIVER] にひょうじするパーティクル [PARTICLE]',
        'mcremote.dustParticleSpec': '[RECEIVER] にひょうじするダスト あか [RED] みどり [GREEN] あお [BLUE] おおきさ [SIZE]',
        'mcremote.blockParticleSpec': '[RECEIVER] にひょうじするブロックのパーティクル [BLOCK] じょうたい [STATE]',
        'mcremote.entityProperty.handle': 'ハンドル',
        'mcremote.entityProperty.type': 'しゅるい',
        'mcremote.particleReceiver.world': 'ちかくのプレイヤー',
        'mcremote.particleReceiver.self': 'せつぞくしたプレイヤーだけ',
        'mcremote.playerDirection': 'プレイヤーのむき [AXIS]',
        'mcremote.setPlayerDirection': 'プレイヤーのむきを x[X]  y[Y]  z[Z] にする',
        'mcremote.entityDirection': 'エンティティ [HANDLE] のむき [AXIS]',
        'mcremote.setEntityDirection': 'エンティティ [HANDLE] のむきを x[X]  y[Y]  z[Z] にする',
        'mcremote.strikeLightning': 'かみなりを x :[X]  y :[Y]  z :[Z] におとす',
        'mcremote.playSound': 'おと [SOUND] を x :[X]  y :[Y]  z :[Z] でならす せってい [OPTIONS]',
        'mcremote.playBlockSound': 'x :[X]  y :[Y]  z :[Z] のブロックの [KIND] おとをならす せってい [OPTIONS]',
        'mcremote.soundOptions': 'おとのせってい おんりょう [VOLUME] たかさ [HEIGHT] ならすあいて [RECEIVER]',
        'mcremote.soundKind.place': 'おく',
        'mcremote.soundKind.hit': 'たたく',
        'mcremote.soundKind.break': 'こわす',
        'mcremote.soundKind.step': 'あしおと',
        'mcremote.soundKind.fall': 'ちゃくち',
        'mcremote.soundReceiver.world': 'ちかくのプレイヤー',
        'mcremote.soundReceiver.self': 'せつぞくしたプレイヤーだけ',
        'mcremote.whenPickaxePoke': 'つるはしでブロックがつつかれたとき',
        'mcremote.whenChatPosted': 'チャットがとうこうされたとき',
        'mcremote.whenProjectileHit': 'とびどうぐがあたったとき',
        'mcremote.eventValue': 'このMinecraftイベントの [PROPERTY]',
        'mcremote.eventValue.sequence': 'とおしばんごう',
        'mcremote.eventValue.type': 'イベントのしゅるい',
        'mcremote.eventValue.dimension': 'じげん',
        'mcremote.eventValue.x': 'xざひょう',
        'mcremote.eventValue.y': 'yざひょう',
        'mcremote.eventValue.z': 'zざひょう',
        'mcremote.eventValue.originX': 'げんてんx',
        'mcremote.eventValue.originY': 'げんてんy',
        'mcremote.eventValue.originZ': 'げんてんz',
        'mcremote.eventValue.face': 'めん',
        'mcremote.eventValue.hand': 'て',
        'mcremote.eventValue.item': 'アイテム',
        'mcremote.eventValue.message': 'メッセージ',
        'mcremote.eventValue.block': 'ブロックじょうほう',
        'mcremote.eventValue.projectile': 'とびどうぐ',
        'mcremote.eventValue.targetKind': 'たいしょうのしゅるい',
        'mcremote.eventValue.targetX': 'たいしょうのxざひょう',
        'mcremote.eventValue.targetY': 'たいしょうのyざひょう',
        'mcremote.eventValue.targetZ': 'たいしょうのzざひょう',
        'mcremote.eventValue.targetFace': 'たいしょうのめん',
        'mcremote.eventValue.targetBlock': 'たいしょうのブロックじょうほう',
        'mcremote.eventValue.targetHandle': 'たいしょうのエンティティハンドル',
        'mcremote.eventStatus': 'イベントじゅしんの [PROPERTY]',
        'mcremote.eventStatus.cursor': 'カーソル',
        'mcremote.eventStatus.latest': 'さいしんのとおしばんごう',
        'mcremote.eventStatus.overflow': 'ほじはんいがいのけつらくすう',
        'mcremote.eventStatus.capacity': 'ようりょうちょうかのけつらくすう',
        'mcremote.eventStatus.discarded': 'めいじはきすう',
        'mcremote.eventStatus.totalLoss': 'けつらくごうけい',
        'mcremote.playerAttribute': 'プレイヤーの [PROPERTY]',
        'mcremote.playerAttribute.dimension': 'じげん',
        'mcremote.playerAttribute.x': 'xざひょう',
        'mcremote.playerAttribute.y': 'yざひょう',
        'mcremote.playerAttribute.z': 'zざひょう',
        'mcremote.playerAttribute.yaw': 'よこのむき',
        'mcremote.playerAttribute.pitch': 'うえしたのむき',
        'mcremote.setPlayerPos': 'プレイヤーを [DIMENSION] x :[X]  y :[Y]  z :[Z] へうごかす',
        'mcremote.setPlayerPose':
            'プレイヤーを [DIMENSION] x :[X]  y :[Y]  z :[Z] よこのむき:[YAW] うえしたのむき:[PITCH] へうごかす',
        'mcremote.setPlayerXYZ': 'プレイヤーを x :[X]  y :[Y]  z :[Z] へうごかす'
    }
};

export default mcremoteMessages;
